import { Helmet } from "react-helmet";
import "../styles/Policy.css";

function Policy() {
  return (
    <main className="policy-container">
      <Helmet>
        <title>Policies | Lash With Brittany</title>
        <meta
          name="description"
          content="Review Lash With Brittany’s booking, cancellation, deposit, and service policies. Designed to ensure a smooth and professional experience."
        />
        <link rel="canonical" href="https://lashwithbrittany.com/policy" />
      </Helmet>

      {/* ===================== Header ===================== */}
      <div className="policy-header-border">
        <header className="policy-header-card">
          <p className="policy-eyebrow">Please Review</p>
          <h1>Our Policies</h1>
          <p className="policy-intro">
            These policies are in place to ensure a seamless, respectful, and
            professional experience for every client.
          </p>
        </header>
      </div>

      {/* ===================== TOC ===================== */}
      <nav className="policy-toc" aria-label="Policy sections">
        <ul>
          <li><a href="#when-you-arrive">When You Arrive</a></li>
          <li><a href="#health-and-safety">Health and Safety</a></li>
          <li><a href="#no-extra-guests">No Extra Guests</a></li>
          <li><a href="#your-appointment">Your Appointment</a></li>
          <li><a href="#cancellation-policy">Cancellation Policy</a></li>
          <li><a href="#no-shows">No Shows</a></li>
          <li><a href="#deposits">Deposits</a></li>
          <li><a href="#payment-methods">Payment Methods</a></li>
          <li><a href="#preparation-guidelines">Preparation Guidelines</a></li>
          <li><a href="#permanent-jewelry-policies">Permanent Jewelry Policies</a></li>
          <li><a href="#contact-information">Contact Information</a></li>
          <li><a href="#service-satisfaction">Service Satisfaction</a></li>
          <li><a href="#policy-acknowledgment">Policy Acknowledgment</a></li>
        </ul>
      </nav>

      {/* ===================== Policy Sections (updated for clarity) ===================== */}

      <section id="scope" className="policy-section">
        <h2>Scope & Purpose</h2>
        <p>
          These policies describe booking, deposits, cancellations, health & safety,
          payments, and service expectations for Lash With Brittany. They are
          intended to ensure clear communication and a safe, professional
          experience for every client.
        </p>
      </section>

      <section id="appointments" className="policy-section">
        <h2>Appointments & Deposits</h2>
        <ul>
          <li>
            <strong>Scheduling:</strong> Appointments are scheduled online or by
            direct contact. Please choose a time you can reasonably keep.
          </li>
          <li>
            <strong>Deposit:</strong> A deposit of $25–$50 (varies by service)
            is required to secure your appointment and is applied to your
            total. Deposits are non-refundable except where stated below.
          </li>
          <li>
            <strong>Late arrivals:</strong> If you arrive more than 15 minutes
            late, we may shorten your service or reschedule and a 50% fee may
            apply if the service cannot be completed.
          </li>
        </ul>
      </section>

      <section id="cancellations" className="policy-section">
        <h2>Cancellations & No-Shows</h2>
        <ul>
          <li>
            <strong>24‑hour policy:</strong> Cancel or reschedule at least 24
            hours before your appointment to avoid penalties. Late cancellations
            may forfeit part or all of the deposit and incur a 50% service
            charge.
          </li>
          <li>
            <strong>No‑show:</strong> Missing an appointment without notice may
            result in a 100% charge of the scheduled service and forfeiture of
            the deposit.
          </li>
          <li>
            <strong>Emergencies:</strong> We understand emergencies happen. Fees
            may be waived at the technician's discretion with timely
            communication.
          </li>
        </ul>
      </section>

      <section id="payments-refunds" className="policy-section">
        <h2>Payments & Refunds</h2>
        <ul>
          <li>
            <strong>Accepted payments:</strong> Cash, most major credit cards,
            PayPal, and Venmo. A small processing fee may apply for digital
            transactions.
          </li>
          <li>
            <strong>Refunds:</strong> Refunds are limited. Deposits for custom
            items or permanent jewelry are final. If you are unhappy with a
            service, contact us promptly — adjustments are often possible.
          </li>
        </ul>
      </section>

      <section id="health-safety" className="policy-section">
        <h2>Health, Allergies & Patch Tests</h2>
        <ul>
          <li>
            <strong>Medical conditions:</strong> Inform us of any medical
            conditions, pregnancy, medications, or implanted devices that may
            affect services.
          </li>
          <li>
            <strong>Allergies & sensitivities:</strong> If you have known
            allergies (including adhesive or metal sensitivities) disclose
            them before booking. We may require a patch test 48–72 hours prior
            to certain treatments.
          </li>
          <li>
            <strong>Right to refuse:</strong> For safety, the technician may
            refuse or modify service if risks are identified.
          </li>
        </ul>
      </section>

      <section id="permanent-jewelry" className="policy-section">
        <h2>Permanent Jewelry Specifics</h2>
        <ul>
          <li>
            <strong>Age & consent:</strong> Clients must be 18+ with valid ID.
            Minors (13–17) require parent/guardian present to consent.
          </li>
          <li>
            <strong>Materials & sensitivities:</strong> We offer several
            metals; clients with sensitivities should select an appropriate
            metal. We cannot guarantee against individual allergic reactions.
          </li>
          <li>
            <strong>Aftercare & repairs:</strong> Avoid pools, lotions, and
            heavy tugging for 24 hours. Rewelds within 7 days are covered; a
            nominal fee may apply after that.
          </li>
          <li>
            <strong>Final sale:</strong> Custom lengths cut and welded are final
            sale; exchanges and refunds are limited.
          </li>
        </ul>
      </section>

      <section id="aftercare" className="policy-section">
        <h2>Aftercare & Adjustments</h2>
        <ul>
          <li>
            <strong>Retention:</strong> Proper aftercare improves retention —
            follow the technician's instructions for best results.
          </li>
          <li>
            <strong>Adjustment window:</strong> If you experience issues within
            48 hours, contact us for a complimentary adjustment. After that,
            normal service fees apply.
          </li>
        </ul>
      </section>

      <section id="liability" className="policy-section">
        <h2>Liability & Consent</h2>
        <p>
          By receiving services, you consent to the treatment performed and
          acknowledge that results vary. Lash With Brittany is not liable for
          reactions due to undisclosed conditions or failure to follow aftercare.
        </p>
      </section>

      <section id="photos-marketing" className="policy-section">
        <h2>Photos & Marketing</h2>
        <p>
          We may take photos of service results for marketing. We will ask for
          your consent before publishing. Notify us if you do not wish to be
          photographed.
        </p>
      </section>

      <section id="privacy" className="policy-section">
        <h2>Privacy & Data</h2>
        <p>
          We collect contact and booking details necessary to provide services.
          For details on data handling, see our <a href="/privacy">Privacy Policy</a>.
        </p>
      </section>

      <section id="changes" className="policy-section">
        <h2>Policy Changes</h2>
        <p>
          These policies may be updated; we will post changes on the website.
          Continued bookings constitute acceptance of the updated terms.
        </p>
      </section>

      <section id="contact-information" className="policy-section">
        <h2>Contact</h2>
        <p>
          For questions about policies or to discuss special circumstances,
          contact: lashwithbrittany@gmail.com or text/call (585) 331-2162.
        </p>
      </section>

      <section id="policy-acknowledgment" className="policy-section">
        <h2>Acknowledgment</h2>
        <p>
          By booking an appointment you acknowledge that you have read and
          agree to these policies.
        </p>
      </section>
    </main>
  );
}

export default Policy;
